import {
  ContentTestsService,
  WorkspaceResolutionService,
  type ContentTestDto,
  type CreateContentTestDto,
  type PaginatedContentTestListDto,
  type WorkspaceResolutionResponseDto,
} from '@inference.engine/persona-api-client';
import { queryOptions } from '@tanstack/react-query';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { personasHttp } from '@/lib/api/personas-client';
import { logger } from '@/lib/utils/logger';
import { downloadPdfBlob } from '@/shared/api/pdf-download.api';
import { CONTENT_TEST_QUERY_KEYS } from './query-keys';

export interface ContentTestsListParams {
  page?: number;
  pageSize?: number;
}

const EMPTY_PAGE: PaginatedContentTestListDto = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 0,
};

const getAllContentTests = async (
  workspaceId: string,
  params: ContentTestsListParams = {},
): Promise<PaginatedContentTestListDto> => {
  try {
    const response = await ContentTestsService.list({
      client: personasHttp,
      headers: { 'x-selected-workspace-id': workspaceId },
      query: {
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
      },
    });

    return response.data ?? EMPTY_PAGE;
  } catch (error) {
    logger.error('Failed to fetch content tests:', error);
    return EMPTY_PAGE;
  }
};

type GetContentTestByIdOptions = {
  suppressErrorNotification?: boolean;
};

const getContentTestById = async (
  id: string,
  options?: GetContentTestByIdOptions,
): Promise<ContentTestDto | null> => {
  try {
    const response = await ContentTestsService.findOne({
      client: personasHttp,
      path: { id },
      // When the resolution seam is active it owns the not-found UX, so a
      // cross-workspace 404 must not flash the global toast. Opt-in only:
      // every other consumer keeps the pre-seam toast behavior.
      headers: options?.suppressErrorNotification
        ? // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
          { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' }
        : undefined,
    });

    return response.data ?? null;
  } catch (error) {
    logger.error('Failed to fetch content test by id:', error);
    return null;
  }
};

/**
 * Fetch a content test for the standalone `/download/content-test/:id` print page.
 * Unlike `getContentTestById`, this REJECTS on a missing test so the print page
 * shows its "Document not found" state.
 */
export const getContentTestForDownload = async (
  workspaceId: string,
  id: string,
): Promise<ContentTestDto> => {
  const response = await ContentTestsService.findOne({
    client: personasHttp,
    path: { id },
    // persona-api scopes by workspace, so the header must match the test's
    // workspace. The headless render has no store to feed the ambient
    // interceptor, so pass the workspace (from the print page's `?workspaceId=`).
    headers: { 'x-selected-workspace-id': workspaceId },
    throwOnError: true,
  });
  return response.data;
};

/**
 * Download a content test as a server-rendered PDF blob. The endpoint derives
 * the workspace from the test itself, so no workspace param is needed.
 */
export const downloadContentTestPdf = (id: string): Promise<Blob> =>
  // eslint-disable-next-line i18next/no-literal-string -- interpolated into the diagnostic `pdf-download.api.ts` throws; every caller toasts its own translated message
  downloadPdfBlob('content-tests', id, { label: 'Content test' });

export const createContentTest = async (
  workspaceId: string,
  payload: CreateContentTestDto,
): Promise<ContentTestDto> => {
  const response = await ContentTestsService.create({
    client: personasHttp,
    headers: { 'x-selected-workspace-id': workspaceId },
    body: payload,
    throwOnError: true,
  });
  return response.data;
};

export const deleteContentTest = async (id: string): Promise<void> => {
  await ContentTestsService.delete({
    client: personasHttp,
    path: { id },
    throwOnError: true,
  });
};

export const contentTestsQuery = (
  workspaceId: string | null | undefined,
  params: ContentTestsListParams = {},
) =>
  queryOptions({
    queryKey: CONTENT_TEST_QUERY_KEYS.list(workspaceId!, params),
    queryFn: () => getAllContentTests(workspaceId!, params),
    enabled: !!workspaceId,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

type ContentTestQueryOptions = {
  suppressErrorNotification?: boolean;
};

export const contentTestQuery = (
  id: string | null | undefined,
  options?: ContentTestQueryOptions,
) =>
  queryOptions({
    // `options` only tunes toast suppression on the request — the fetched data
    // is identical, so it must NOT split the cache bucket.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: CONTENT_TEST_QUERY_KEYS.detail(id!),
    queryFn: () =>
      getContentTestById(id!, { suppressErrorNotification: options?.suppressErrorNotification }),
    enabled: !!id,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

const resolveContentTestWorkspace = async (id: string): Promise<WorkspaceResolutionResponseDto> => {
  const response = await WorkspaceResolutionService.resolveContentTestWorkspace({
    client: personasHttp,
    path: { id },
    // The resolution seam maps a 404 to its own generic "not available"
    // state; the global toast would leak the raw error first.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to resolve content test');
  }

  return response.data;
};

/**
 * Deep-link resolution of a content-test id to its owning workspace. Runs
 * BEFORE the workspace-scoped `contentTestQuery` (via `useDeepLinkResolution`)
 * so a deep link from another workspace doesn't 404 under the ambient
 * `x-selected-workspace-id` header.
 */
export const contentTestResolutionQuery = (id?: string) =>
  queryOptions({
    queryKey: CONTENT_TEST_QUERY_KEYS.resolution(id ?? ''),
    queryFn: () => resolveContentTestWorkspace(id!),
  });
